import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Lightning, Sparkle, PhoneCall } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import BuzzLogo from "../components/BuzzLogo";
import LiveTicker from "../components/LiveTicker";
import CostCalculator from "../components/CostCalculator";

const RECOMMENDED_KEY = "complete";

export default function Pricing() {
  const { business } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ plans: {}, one_time_addons: {} });
  const [billing, setBilling] = useState("annual"); // default "annual" so the 2-months-free savings pop first
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const buyPlan = async (planKey) => {
    if (!business) { nav("/signup"); return; }
    setLoading(planKey);
    try {
      const r = await api.post("/plans/checkout", { plan_key: planKey, billing, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(null);
    }
  };

  const buyWhiteGlove = async () => {
    if (!business) { nav("/signup"); return; }
    setLoading("whiteglove");
    try {
      const r = await api.post("/addons/whiteglove/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(null);
    }
  };

  const plans = data.plans || {};
  const wg = data.one_time_addons?.a2p_whiteglove;

  return (
    <div className="min-h-screen bg-[#F7F6F2] font-body">
      {/* Nav */}
      <header className="fixed top-6 left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="backdrop-blur-xl bg-white/75 ring-1 ring-black/5 rounded-full pl-3 pr-3 py-2 flex items-center justify-between shadow-[0_10px_40px_rgba(30,58,47,0.06)]">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0E1512] flex items-center justify-center"><BuzzLogo size={20} /></div>
              <span className="font-heading font-extrabold text-lg tracking-tight text-[#121914]">BuzzerAI</span>
            </Link>
            <div className="flex items-center gap-2 text-sm">
              <Link to={business ? "/dashboard" : "/login"} className="px-3 py-1.5 rounded-full hover:bg-black/5 transition-colors text-[#121914]">{business ? "Dashboard" : "Log in"}</Link>
              {!business && <Link to="/signup" className="inline-flex items-center gap-1.5 bg-[#0E1512] text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#1E3A2F] transition-colors">Start free <ArrowRight size={14} weight="bold" /></Link>}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-6 px-6 relative overflow-hidden">
        <div className="absolute -right-64 -top-64 w-[720px] h-[720px] rounded-full bg-[#F5C543]/12 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 hex-bg pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-semibold mb-3 inline-flex items-center gap-2 justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D96C5B]" /> Straight pricing · No per-cover fees
          </div>
          <h1 className="font-heading text-5xl md:text-7xl tracking-[-0.03em] leading-[0.95] mb-4 text-[#0E1512]">
            Pick your <span className="honey-bar italic text-[#D96C5B]">buzz</span>.
          </h1>
          <p className="text-[#5C6B61] text-base sm:text-lg max-w-2xl mx-auto leading-snug">
            Waitlist stays free forever. Everything else scales with your restaurant. One saved dinner pays for the month.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center gap-1 p-1 rounded-full bg-white ring-1 ring-black/10" data-testid="billing-toggle">
            <button
              onClick={() => setBilling("monthly")}
              data-testid="billing-monthly"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${billing === "monthly" ? "bg-[#0E1512] text-white" : "text-[#5C6B61] hover:text-[#121914]"}`}>Monthly</button>
            <button
              onClick={() => setBilling("annual")}
              data-testid="billing-annual"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${billing === "annual" ? "bg-[#0E1512] text-white" : "text-[#5C6B61] hover:text-[#121914]"}`}>
              Annual
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${billing === "annual" ? "bg-[#F5C543] text-[#0E1512]" : "bg-[#F5C543]/20 text-[#0E1512]"}`}>2 mo free</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plan grid */}
      <section className="px-6 pb-14">
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-3 md:gap-4 items-stretch">
          {/* Free plan card */}
          <div className="bg-white ring-1 ring-black/5 rounded-2xl p-6 flex flex-col" data-testid="plan-free">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6B61] font-bold mb-2">Free</div>
            <div className="font-heading text-2xl text-[#121914] mb-1">Waitlist Core</div>
            <p className="text-xs text-[#5C6B61] leading-snug mb-4 min-h-[36px]">Everything you need to run a live waitlist forever.</p>
            <div className="flex items-baseline gap-1 mb-5">
              <span className="font-heading text-4xl text-[#121914]">$0</span>
              <span className="text-xs text-[#5C6B61] font-mono">/forever</span>
            </div>
            <ul className="space-y-1.5 text-xs text-[#121914] flex-1">
              {["Live queue + walk-ins", "SMS \"table ready\" alerts", "Public join link + QR", "Analytics dashboard"].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check size={14} weight="bold" color="#1E3A2F" className="mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Link to={business ? "/dashboard" : "/signup"} className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm text-[#121914] border border-black/10 hover:border-[#121914] px-4 py-2.5 rounded-full font-semibold transition-colors" data-testid="plan-free-cta">
              {business ? "Go to dashboard" : "Start free"} <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          {/* Paid plan cards */}
          {Object.values(plans).map((plan) => {
            const isRec = plan.key === RECOMMENDED_KEY;
            const price = billing === "annual" ? plan.annual_price : plan.monthly_price;
            const perLabel = billing === "annual" ? "/yr" : "/mo";
            const monthlyEquiv = billing === "annual" ? Math.round(plan.annual_price / 12) : null;
            return (
              <div
                key={plan.key}
                data-testid={`plan-${plan.key}`}
                className={`relative rounded-2xl p-6 flex flex-col transition-transform hover:-translate-y-1 ${
                  isRec
                    ? "bg-[#0E1512] text-white ring-2 ring-[#F5C543] shadow-[0_20px_60px_rgba(30,58,47,0.15)]"
                    : "bg-white ring-1 ring-black/5"
                }`}
              >
                {isRec && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest bg-[#F5C543] text-[#0E1512] px-3 py-1 rounded-full">
                    Most popular
                  </span>
                )}
                <div className={`text-[11px] tracking-[0.22em] uppercase font-bold mb-2 ${isRec ? "text-[#F5C543]" : "text-[#D96C5B]"}`}>
                  {plan.name}
                </div>
                <div className={`font-heading text-2xl mb-1 ${isRec ? "text-white" : "text-[#121914]"}`}>{plan.tagline}</div>
                <p className={`text-xs leading-snug mb-4 min-h-[36px] ${isRec ? "text-[#C2D1C1]" : "text-[#5C6B61]"}`}>{plan.description}</p>
                <div className="mb-1 flex items-baseline gap-1">
                  <span className={`font-heading text-4xl ${isRec ? "text-white" : "text-[#121914]"}`}>${price}</span>
                  <span className={`text-xs font-mono ${isRec ? "text-[#C2D1C1]" : "text-[#5C6B61]"}`}>{perLabel}</span>
                </div>
                {monthlyEquiv && (
                  <div className={`text-[10px] font-mono mb-4 ${isRec ? "text-[#F5C543]" : "text-[#5C6B61]"}`}>
                    ~${monthlyEquiv}/mo · 2 months free
                  </div>
                )}
                {!monthlyEquiv && <div className="text-[10px] font-mono mb-4 opacity-0">.</div>}
                <ul className={`space-y-1.5 text-xs flex-1 ${isRec ? "text-white" : "text-[#121914]"}`}>
                  {(plan.highlights || []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} weight="bold" color={isRec ? "#F5C543" : "#1E3A2F"} className="mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => buyPlan(plan.key)}
                  disabled={loading === plan.key}
                  data-testid={`plan-${plan.key}-cta`}
                  className={`mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3 rounded-full transition-all disabled:opacity-60 ${
                    isRec
                      ? "bg-[#F5C543] text-[#0E1512] hover:bg-[#F7D470]"
                      : "bg-[#0E1512] text-white hover:bg-[#1E3A2F]"
                  }`}
                >
                  {loading === plan.key ? "Redirecting…" : (business ? `Choose ${plan.name}` : "Start free trial")}
                  <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* White-glove add-on */}
      {wg && (
        <section className="px-6 pb-14">
          <div className="max-w-5xl mx-auto rounded-2xl bg-[#1E3A2F] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 relative overflow-hidden" data-testid="whiteglove-addon">
            <div className="absolute -right-32 -top-32 w-80 h-80 rounded-full bg-[#F5C543]/12 blur-3xl" />
            <div className="w-12 h-12 rounded-xl bg-[#F5C543] flex items-center justify-center shrink-0 relative">
              <Sparkle size={22} color="#0E1512" weight="fill" />
            </div>
            <div className="flex-1 relative">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#F5C543] font-bold">Optional one-time · $40</div>
              <div className="font-heading text-2xl md:text-3xl mt-1">{wg.name}</div>
              <p className="text-sm text-[#C2D1C1] mt-1 leading-snug max-w-lg">{wg.description}</p>
            </div>
            <button
              onClick={buyWhiteGlove}
              disabled={loading === "whiteglove"}
              data-testid="whiteglove-cta"
              className="relative inline-flex items-center gap-2 bg-[#F5C543] text-[#0E1512] text-sm font-bold px-5 py-3 rounded-full hover:bg-[#F7D470] transition-all disabled:opacity-60"
            >
              {loading === "whiteglove" ? "Redirecting…" : "Add for $40"} <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </section>
      )}

      {/* Honest all-in cost widget — mirrors the transparent FAQ disclosure so
          prospects see the real Twilio + BuzzerAI total right on the pricing page
          instead of doing the math themselves and imagining worst-case numbers. */}
      <section className="px-6 pb-14" data-testid="pricing-cost-widget">
        <div className="max-w-5xl mx-auto bg-white ring-1 ring-black/5 rounded-2xl p-6 md:p-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-semibold mb-2">Your all-in monthly cost</div>
          <h2 className="font-heading text-2xl md:text-4xl text-[#121914] leading-tight mb-3 max-w-3xl">
            Two bills. <span className="text-[#5C6B61]">Ours, and Twilio's.</span> No markups.
          </h2>
          <p className="text-sm text-[#5C6B61] max-w-2xl mb-6">
            Every restaurant pays Twilio directly for its own phone number and text messages — we don't touch those dollars.
            Here's the honest total for a typical indie restaurant on the <b>Complete plan ($49/mo)</b>:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="pricing-cost-table">
              <thead>
                <tr className="bg-[#F7F6F2] text-left">
                  <th className="border border-[#E1DFD5] px-3 py-2">Traffic</th>
                  <th className="border border-[#E1DFD5] px-3 py-2">Twilio / mo</th>
                  <th className="border border-[#E1DFD5] px-3 py-2">BuzzerAI Complete</th>
                  <th className="border border-[#E1DFD5] px-3 py-2"><b>Total / mo</b></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-[#E1DFD5] px-3 py-2">Slow (5 calls/day · 5 SMS/day)</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-mono">~$16</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-mono">$49</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-bold text-[#1E3A2F] font-mono">~$65</td>
                </tr>
                <tr className="bg-[#F7F6F2]/50">
                  <td className="border border-[#E1DFD5] px-3 py-2"><b>Typical dinner service</b> (12 calls/day · 15 SMS/day)</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-mono">~$22</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-mono">$49</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-bold text-[#1E3A2F] font-mono">~$71</td>
                </tr>
                <tr>
                  <td className="border border-[#E1DFD5] px-3 py-2">Busy (25 calls/day · 30 SMS/day)</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-mono">~$32</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-mono">$49</td>
                  <td className="border border-[#E1DFD5] px-3 py-2 font-bold text-[#1E3A2F] font-mono">~$81</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-[#5C6B61] italic max-w-lg">
              Twilio rates: $1.15/mo number · $0.0085/min voice · $0.0079/SMS · $10/mo A2P compliance.
              One-time A2P brand registration $4–44 paid once to Twilio at signup.
            </p>
            <Link to="/faq" className="text-sm text-[#0E1512] font-semibold underline decoration-[#F5C543] decoration-2 underline-offset-4">
              See every line item →
            </Link>
          </div>

          <CostCalculator />
        </div>
      </section>

      {/* Value proof strip — verifiable product facts and math only.
          No third-party statistics unless we can cite the primary source. */}
      <section className="px-6 pb-14">
        <div className="max-w-5xl mx-auto bg-white ring-1 ring-black/5 rounded-2xl p-6 md:p-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#5C6B61] font-semibold mb-2">Why $49/mo pays for itself</div>
          <h2 className="font-heading text-2xl md:text-4xl text-[#121914] leading-tight mb-6 max-w-3xl">
            <span className="honey-bar text-[#D96C5B]">One party of 3</span> covers a whole month of Complete. Every other seated party is upside.
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              ["1 party",  "of 3 at $50 average check covers a whole month",   "arithmetic on $49/mo"],
              ["$49/mo",   "flat — no per-cover fees, no contract, no lock-in", "our pricing page"],
              ["14 days",  "free trial, no card, cancel in one tap",             "our signup terms"],
            ].map(([n, c, s]) => (
              <div key={c} className="border-l-2 border-[#F5C543] pl-4">
                <div className="font-heading text-3xl text-[#1E3A2F] leading-none">{n}</div>
                <p className="text-xs text-[#5C6B61] mt-2 leading-snug">{c}</p>
                <p className="text-[10px] font-mono text-[#5C6B61]/70 mt-1 italic">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Owner promise */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto bg-[#C2D1C1]/25 ring-1 ring-[#1E3A2F]/15 rounded-2xl p-6 md:p-8 flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0">
            <PhoneCall size={22} color="#D96C5B" weight="fill" />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-bold mb-2">Our promise</div>
            <p className="text-[#1E3A2F] leading-relaxed">
              Hospitality runs on razor-thin margins. Every dollar you spend on software is one not going to your team, your ingredients, or your rent. If BuzzerAI isn't paying for itself in your first 30 days, <b>cancel in one tap</b>. No contracts. No exit fees. No sales calls.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
